import type { Binding } from '@wix/wix-data-client-types'
import { pairBindingsWithProps } from './pairBindingsWithProps'
import type { DataBindingComponentModel } from '@wix/data-binding-registry-sdk'
import type { ComponentBinding } from './appApiTypes'

type BindingWithDefinition = {
  bindingDefinition: DataBindingComponentModel
  connections: ComponentBinding[]
}

type Props = {
  bindingsWithDefinitions: BindingWithDefinition[]
  predicate: (binding: Binding) => boolean
  filterFn?: (binding: Binding) => boolean
}

export const checkSomeBindings = ({
  bindingsWithDefinitions,
  predicate,
  filterFn = () => true,
}: Props) =>
  bindingsWithDefinitions.some(({ bindingDefinition, connections }) =>
    connections.some(connection => {
      const boundSection = bindingDefinition.sections.find(
        section => section.role === connection.role,
      )!

      const pairedBindings = pairBindingsWithProps(
        connection.bindings,
        boundSection.bindings,
      )

      return pairedBindings
        .filter(({ config }) => config)
        .filter(filterFn)
        .some(predicate)
    }),
  )

export const checkAllBindings = ({
  bindingsWithDefinitions,
  predicate,
  filterFn,
}: Props) =>
  bindingsWithDefinitions.every(({ bindingDefinition, connections }) =>
    connections.every(connection => {
      const boundSection = bindingDefinition.sections.find(
        section => section.role === connection.role,
      )!

      const pairedBindings = pairBindingsWithProps(
        connection.bindings,
        boundSection.bindings,
      )

      return pairedBindings
        .filter(({ config }) => config)
        .filter(filterFn || (() => true))
        .every(predicate)
    }),
  )

export const isInputBinding = ({ definition, config }: Binding) =>
  ('isInputProperty' in definition && Boolean(definition.isInputProperty)) ||
  config?.type === 'Filter'

export const isDisplayElementBinding = ({ definition, config }: Binding) =>
  (!('isInputProperty' in definition) || !definition.isInputProperty) &&
  config?.type !== 'Filter'

export const isUseInputFilter = ({ config }: Binding) =>
  config?.type === 'Filter'
